// System Settings page -- iteration 076
// Super-admin only. Displays DB-backed settings grouped by category.

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import * as api from "@/lib/api";
import type { SystemSettingGroupResponse, SystemSettingPublic } from "@dugads/shared";

export default function SettingsPage() {
  const location = useLocation();
  const [groups, setGroups] = useState<SystemSettingGroupResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .getSettings()
      .then((res) => {
        setGroups(res.groups);
        setError("");
      })
      .catch((err) => setError(err.message ?? "Failed to load settings"))
      .finally(() => setLoading(false));
  }, [location.key]);

  if (loading) {
    return (
      <div data-page="settings" className="p-6">
        <h1 className="text-2xl font-semibold mb-6">System Settings</h1>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div data-page="settings" className="p-6">
        <h1 className="text-2xl font-semibold mb-6">System Settings</h1>
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div data-page="settings" className="p-6 max-w-3xl">
      <h1 className="text-2xl font-semibold mb-6">System Settings</h1>
      {groups.length === 0 && (
        <p className="text-muted-foreground">No settings configured.</p>
      )}
      {groups.map((group) => (
        <SettingsGroup key={group.group} group={group} onUpdated={() => {
          api.getSettings().then((res) => setGroups(res.groups));
        }} />
      ))}
    </div>
  );
}

// Settings that have predefined options render as dropdowns
const SETTING_OPTIONS: Record<string, { value: string; label: string }[]> = {
  "email.provider": [
    { value: "resend", label: "Resend" },
    { value: "sendgrid", label: "SendGrid" },
    { value: "smtp", label: "SMTP" },
  ],
};

// Settings that should appear first in their group (by key)
const SETTING_SORT_ORDER: Record<string, number> = {
  "email.provider": 0,
  "email.api_key": 1,
  "email.from_address": 2,
  "email.webhook_secret": 3,
};

function settingSortKey(key: string): number {
  return SETTING_SORT_ORDER[key] ?? 99;
}

function SettingsGroup({ group, onUpdated }: { group: SystemSettingGroupResponse; onUpdated: () => void }) {
  const sorted = [...group.settings].sort((a, b) => settingSortKey(a.key) - settingSortKey(b.key));
  return (
    <div className="mb-8">
      <h2 className="text-lg font-medium capitalize mb-3 border-b pb-2">{group.group}</h2>
      <div className="space-y-4">
        {sorted.map((setting) => (
          <SettingRow key={setting.key} setting={setting} onUpdated={onUpdated} />
        ))}
      </div>
    </div>
  );
}

function SettingRow({ setting, onUpdated }: { setting: SystemSettingPublic; onUpdated: () => void }) {
  const options = SETTING_OPTIONS[setting.key];
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  function startEdit() {
    setValue(setting.is_secret ? "" : setting.value);
    setEditing(true);
    setError("");
  }

  async function save(newValue?: string) {
    const val = newValue ?? value;
    setSaving(true);
    setError("");
    try {
      await api.updateSetting(setting.key, val);
      setEditing(false);
      onUpdated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  // Dropdown settings save immediately on change
  if (options) {
    const currentLabel = options.find((o) => o.value === setting.value)?.label ?? setting.value;
    return (
      <div data-field={setting.key} className="flex flex-col gap-1">
        <label className="text-sm font-medium text-foreground">{setting.label}</label>
        <p className="text-xs text-muted-foreground">{setting.key}</p>
        <div className="flex items-center gap-2">
          <select
            value={setting.value}
            onChange={(e) => save(e.target.value)}
            disabled={saving}
            title={setting.label}
            className="rounded-md border px-3 py-1.5 text-sm bg-background disabled:opacity-50"
          >
            {!options.some((o) => o.value === setting.value) && (
              <option value={setting.value}>{currentLabel}</option>
            )}
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {saving && <span className="text-xs text-muted-foreground">Saving...</span>}
        </div>
        {error && <p className="text-xs text-destructive">{error}</p>}
      </div>
    );
  }

  return (
    <div data-field={setting.key} className="flex flex-col gap-1">
      <label className="text-sm font-medium text-foreground">{setting.label}</label>
      <p className="text-xs text-muted-foreground">{setting.key}</p>
      {!editing ? (
        <div className="flex items-center gap-2">
          <span className="text-sm text-foreground font-mono bg-muted px-2 py-1 rounded min-w-0 truncate">
            {setting.value || <span className="text-muted-foreground italic">not set</span>}
          </span>
          <button
            type="button"
            data-action={`edit-${setting.key}`}
            onClick={startEdit}
            className="text-xs text-primary hover:underline shrink-0"
          >
            Edit
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <input
            type={setting.is_secret ? "password" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={setting.is_secret ? "Enter new value" : ""}
            className="flex-1 rounded-md border px-3 py-1.5 text-sm font-mono bg-background"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          />
          <button
            type="button"
            data-action={`save-${setting.key}`}
            onClick={() => save()}
            disabled={saving}
            className="text-xs text-primary hover:underline shrink-0 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
          <button
            type="button"
            onClick={() => setEditing(false)}
            className="text-xs text-muted-foreground hover:underline shrink-0"
          >
            Cancel
          </button>
        </div>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
